import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button, Props as ButtonProps } from 'atoms/buttons/Button';
import { LoadingButton } from 'atoms/buttons/LoadingButton';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyM } from 'atoms/typography';
import { Dialog } from 'components/dialog';
import { glossary } from 'lib/glossary';

const Message = styled(BodyM)`
  text-align: center;
  font-weight: normal;
  margin-bottom: var(--double-unit);
`;

type Props = {
  onConfirm: (() => void) | (() => Promise<void>);
  title?: ReactNode;
  subtitle?: ReactNode;
  message?: ReactNode;
  cancelCta?: ReactNode;
  cta?: ReactNode;
  ctaProps?: Omit<ButtonProps, 'size'> & { size?: never };
  open: boolean;
  onClose: () => void;
  fullScreen?: boolean;
  hideCancel?: boolean;
};
export const ConfirmDialog = ({
  title,
  subtitle,
  message,
  onConfirm,
  cancelCta,
  cta,
  ctaProps,
  open,
  onClose,
  fullScreen,
  hideCancel = false,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const confirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      footer={
        <Horizontal gap={3} center>
          {!hideCancel && (
            <Button
              disabled={loading}
              size="medium"
              onClick={onClose}
              color="quaternary"
              fullWidth
            >
              {cancelCta || <FormattedMessage {...glossary.close} />}
            </Button>
          )}
          <LoadingButton
            loading={loading}
            size="medium"
            color="primary"
            onClick={confirm}
            fullWidth
            {...ctaProps}
          >
            {cta || (
              <FormattedMessage
                id="ConfirmDialog.confirm"
                defaultMessage="Confirm"
              />
            )}
          </LoadingButton>
        </Horizontal>
      }
    >
      <Vertical gap={2}>
        {title && <div className="text-center">{title}</div>}
        <Vertical gap={2}>
          {subtitle}
          {message || (
            <Message>
              <FormattedMessage
                id="ConfirmDialog.message"
                defaultMessage="Are you sure?"
              />
            </Message>
          )}
        </Vertical>
      </Vertical>
    </Dialog>
  );
};
